import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink } from "@apollo/client";

import {
  getErrorLink,
  getQueryLoadingLink,
  getUploadLink,
  getPersistedQueriesLink,
} from "@shared/data/client";
import { useFlippers, useLoadingData, useServer } from "@shared/hooks";
import { getMutationLoadingLink, isDevelopment } from "@shared/utils";

import { getCache } from "./openCache";

const useClient = (): ApolloClient<NormalizedCacheObject> => {
  const { incrementMutationLoading, decrementMutationLoading } = useLoadingData();
  const [gqlPersistedQueriesFlipper] = useFlippers("gql_persisted_queries");
  const { ssr } = useServer();

  const getLink = () => {
    const errorLink = getErrorLink();
    const uploadLink = getUploadLink();
    const mutationLoadingLink = getMutationLoadingLink({
      incrementMutationLoading,
      decrementMutationLoading,
    });

    let links = [errorLink, mutationLoadingLink];
    if (gqlPersistedQueriesFlipper) {
      const persistedQueriesLink = getPersistedQueriesLink();
      links.push(persistedQueriesLink);
    }

    if (isDevelopment()) {
      const queryLoadingLink = getQueryLoadingLink();
      links = [...links, queryLoadingLink];
    }

    return ApolloLink.from([...links, uploadLink]);
  };

  const getClient = () => {
    return new ApolloClient({
      link: getLink(),
      cache: getCache({ ssr }),
    });
  };

  return getClient();
};

export { useClient };
